import React, {FunctionComponent, useEffect, useRef, useState} from "react";
import {makeStyles} from "tss-react/mui";
import {Grid, Popover, Theme} from "@mui/material";
import moment from "moment/moment";
import {useTranslation} from "@/Utils/CustomHooks/useTranslation";

interface BookingPopoverProps {
    lineItem: any;
    popoverPositionX: number;
    popoverPositionY: number;
    open: boolean;
    handlePopoverClose: () => void;
}

const useStyles = makeStyles()((theme: Theme) => ({
    popover: {
        pointerEvents: "none",
    },

    paper: {
        padding: theme.spacing(1),
    },
}));

const BookingPopover: FunctionComponent<BookingPopoverProps> = ({
                                                                    lineItem,
                                                                    popoverPositionX,
                                                                    popoverPositionY,
                                                                    open,
                                                                    handlePopoverClose,
                                                                }): JSX.Element => {
    const {classes} = useStyles();
    const ref = useRef(null);
    const {t} = useTranslation();

    const [isDelaying, setIsDelaying] = useState(true);
    const [delayRunning, setDelayRunning] = useState(false);
    const [rerenderOnce, setRerenderOnce] = useState(false);

    if (rerenderOnce) {
        // Necessary for correct initial position
        setRerenderOnce(false);
    }

    const isMounted = useRef(false);

    const finishDelay = () => {
        if (isMounted.current) {
            setIsDelaying(false);
            setRerenderOnce(true);
        }
    };

    if (isDelaying && !delayRunning) {
        setDelayRunning(true);
        setTimeout(finishDelay, 500);
    }

    useEffect(() => {
        // Prevent memory leak
        isMounted.current = true;
        return () => {
            isMounted.current = false;
        };
    }, []);

    const bodyWidth =
        document?.getElementById("content-container")?.offsetWidth || 0;
    const bodyHeight =
        document?.getElementById("content-container")?.offsetHeight || 0;

    let horizontal = "left";
    let vertical = "top";
    // @ts-ignore
    if (popoverPositionX + ref?.current?.clientWidth >= bodyWidth) {
        horizontal = "right";
    }
    // @ts-ignore
    if (popoverPositionY + ref?.current?.offsetHeight >= bodyHeight) {
        vertical = "bottom";
    }

    let posY = popoverPositionY;
    if (vertical === "bottom") {
        posY -= 20;
    } else {
        posY += 20;
    }

    if (isDelaying) {
        return <></>;
    }

    return (
        <Popover
            anchorReference="anchorPosition"
            anchorPosition={{top: posY, left: popoverPositionX}}
            anchorOrigin={{
                // @ts-ignore
                vertical: vertical,
                // @ts-ignore
                horizontal: horizontal,
            }}
            transformOrigin={{
                // @ts-ignore
                vertical: vertical,
                // @ts-ignore
                horizontal: horizontal,
            }}
            className={classes.popover}
            classes={{
                paper: classes.paper,
            }}
            open={open}
            onClose={handlePopoverClose}
            disableRestoreFocus
        >
            <div ref={ref}>
                <Grid container spacing={0} style={{marginBottom: 10}}>
                    <Grid item xs={4}>
                        {t("popover.booking.bookingId")}:
                    </Grid>
                    <Grid item xs={8}>
                        <b>{lineItem?.bookingId}</b>
                    </Grid>
                    <Grid item xs={4}>
                        {t("popover.booking.reservationNumber")}:
                    </Grid>
                    <Grid item xs={8}>
                        <b>{lineItem?.reservationNumber}</b>
                    </Grid>
                </Grid>
                <Grid container spacing={0} style={{marginBottom: 10}}>
                    <Grid item xs={4}>
                        {t("popover.booking.customerId")}:
                    </Grid>
                    <Grid item xs={8}>
                        <b>{lineItem.customer?.customerId}</b>
                    </Grid>
                    <Grid item xs={4}>
                        {t("popover.booking.customer")}:
                    </Grid>
                    <Grid item xs={8}>
                        <b>{lineItem.customer?.name1}</b>
                    </Grid>
                </Grid>
                <Grid container spacing={0} style={{marginBottom: 10}}>
                    <Grid item xs={4}>
                        {t("popover.booking.reservedGroup")}:
                    </Grid>
                    <Grid item xs={8}>
                        <b>
                            {lineItem?.vehicleGroup?.vehicleGroupId} -{" "}
                            {lineItem?.vehicleGroup?.description}
                        </b>
                    </Grid>
                </Grid>
                <Grid container spacing={0} style={{marginBottom: 10}}>
                    <Grid item xs={4}>
                        {t("popover.booking.departure")}:
                    </Grid>
                    <Grid item xs={8}>
                        <b>
                            {lineItem?.startstation?.stationId || lineItem?.startstation} -{" "}
                            {moment(lineItem?.startdate).format(
                                "DD.MM.YYYY HH:mm",
                            )}
                        </b>
                    </Grid>
                    {lineItem?.enddate && lineItem?.endstation ? (
                        <>
                            <Grid item xs={4}>
                                {t("popover.booking.arrival")}:
                            </Grid>
                            <Grid item xs={8}>
                                <b>
                                    {lineItem?.endstation?.stationId || lineItem?.endstation} -{" "}
                                    {moment(lineItem?.enddate).format(
                                        "DD.MM.YYYY HH:mm",
                                    )}
                                </b>
                            </Grid>
                        </>
                    ) : (
                        <>
                            <Grid item xs={4}>
                                {t("popover.booking.plannedArrival")}:
                            </Grid>
                            <Grid item xs={8}>
                                <b>
                                    {lineItem?.plannedEndstation?.stationId || lineItem?.plannedEndstation} -{" "}
                                    {moment(lineItem?.plannedEnddate).format(
                                        "DD.MM.YYYY HH:mm",
                                    )}
                                </b>
                            </Grid>
                        </>
                    )}
                    <Grid item xs={4}>
                        {t("popover.booking.delivery")}:
                    </Grid>
                    <Grid item xs={8}>
                        {lineItem?.deliveryDate ? (
                            <b>
                                {moment(lineItem?.deliveryDate).format(
                                    "DD.MM.YYYY HH:mm",
                                )}
                            </b>
                        ) : (
                            <b>/</b>
                        )}
                    </Grid>
                    <Grid item xs={4}>
                        {t("popover.booking.pickUp")}:
                    </Grid>
                    <Grid item xs={8}>
                        {lineItem?.pickupDate ? (
                            <b>
                                {moment(lineItem?.pickupDate).format(
                                    "DD.MM.YYYY HH:mm",
                                )}
                            </b>
                        ) : (
                            <b>/</b>
                        )}
                    </Grid>
                </Grid>
                <Grid container spacing={0} style={{marginBottom: 10}}>
                    <Grid item xs={4}>
                        {t("popover.booking.rate")}:
                    </Grid>
                    <Grid item xs={8}>
                        <b>{lineItem?.rate}</b>
                    </Grid>
                    <Grid item xs={4}>
                        {t("popover.booking.miscellaneous")}:
                    </Grid>
                    <Grid item xs={8}>
                        <b>{lineItem?.divdata}</b>
                    </Grid>
                </Grid>
            </div>
        </Popover>
    );
};

export default BookingPopover;
